import React, { FC } from 'react';
import { TextField } from '@mui/material';
import {
  PaymentElement,
  AddressElement,
  LinkAuthenticationElement,
} from '@stripe/react-stripe-js';
import { StripeAddressElementChangeEvent, StripeAddressElementOptions } from '@stripe/stripe-js';

/** Mirrors Stripe's AddressElement defaultValues (note: address.country is required). */
export type IAddressDefaults = StripeAddressElementOptions['defaultValues'];

interface IPaymentElementFieldsProps {
  /** Post/Reactivate/Upgrade show a Billing Company field; Prepaid does not. */
  showCompany: boolean;
  /** Seeds the Link email input (Step7: defaultData.billingEmail, Prepaid: step2.yourEmail). */
  defaultEmail?: string;
  /** Controlled Billing Company value (only when showCompany). */
  company?: string;
  /** Optional AddressElement prefill. */
  addressDefaults?: IAddressDefaults;
  /**
   * `complete` is the Link element's own validity flag (a blank or malformed email
   * is not complete). Steps whose payload carries the billing email should gate
   * submit on it, exactly as they already gate on AddressElement's `complete`.
   */
  onEmailChange: (email: string, complete: boolean) => void;
  onCompanyChange?: (company: string) => void;
  onAddressChange: (event: StripeAddressElementChangeEvent) => void;
}

/**
 * The unified billing form: Link email → optional Billing Company (MUI) →
 * Address (name/address/phone) → Payment Element (card + wallets + Link + ACH).
 *
 * Renders INSIDE <Elements> (see JobPaymentElements). Values are surfaced to the
 * step via onChange callbacks — the step keeps the latest email/company/address
 * and feeds them to mapBillingToPayload on submit. We never call getValue()
 * (not part of the stable API on the resolved @stripe/stripe-js 1.42.1).
 */
export const PaymentElementFields: FC<IPaymentElementFieldsProps> = ({
  showCompany,
  defaultEmail,
  company,
  addressDefaults,
  onEmailChange,
  onCompanyChange,
  onAddressChange,
}) => (
  <div data-cy="paymentElementFields">
    <LinkAuthenticationElement
      options={defaultEmail ? { defaultValues: { email: defaultEmail } } : undefined}
      onChange={(e) => onEmailChange(e.value.email, e.complete)}
    />

    {showCompany && (
      <TextField
        fullWidth
        margin="normal"
        label="Billing Company"
        placeholder="Billing Company"
        value={company ?? ''}
        onChange={(e) => onCompanyChange?.(e.target.value)}
        inputProps={{ 'data-cy': 'billingCompany' }}
      />
    )}

    <div style={{ marginTop: '1rem' }}>
      <AddressElement
        options={{
          mode: 'billing',
          fields: { phone: 'always' },
          ...(addressDefaults ? { defaultValues: addressDefaults } : {}),
        }}
        onChange={onAddressChange}
      />
    </div>

    <div style={{ marginTop: '1rem' }}>
      {/*
        Apple Pay / Google Pay render inside the Payment Element as wallet
        buttons when the browser supports them AND the page's domain is
        registered under Stripe → Settings → Payment method domains. Apple Pay
        additionally requires Stripe's domain-association file to be served at
        /.well-known/apple-developer-merchantid-domain-association (shipped from
        public/.well-known, copied verbatim into the CRA build → S3 → CloudFront).
        'auto' is Stripe's default; it is spelled out so nobody has to guess.
      */}
      <PaymentElement
        options={{ wallets: { applePay: 'auto', googlePay: 'auto' } }}
      />
    </div>

    <p className="cpg-mono-note">🔒 SECURED BY STRIPE</p>
  </div>
);

export default PaymentElementFields;
